import { useEffect, useMemo, useState } from 'react';

import _max from 'lodash/max';

import * as Api from '../../../weborder/Api';
import { ProgressBtnType } from '../types.d';
import { Observable } from './observable';

type MenuOptionsState = {
  indexCurrent: number;
  indexHistory: number[];
  optionsDraft: any[];
  optionsHistory: any[][];
  optionsSelected: any[];
  menuOptionSets: any[];
  menuOptionSetCurrent: any;
  progressBtnEnabled: boolean;
  progressBtnType: ProgressBtnType;
};

const initialState: MenuOptionsState = {
  indexCurrent: 0,
  indexHistory: [0],
  optionsHistory: [],
  optionsDraft: [],
  optionsSelected: [],
  menuOptionSets: [],
  menuOptionSetCurrent: undefined,
  progressBtnEnabled: false,
  progressBtnType: ProgressBtnType.NEXT,
};

const moUiStore = new Observable(initialState);

export const useMenuOptionsUiState = (optionSets?: Api.MenuItemOptionSet[]) => {
  const [state, setState] = useState(() => moUiStore.get());

  useEffect(() => {
    moUiStore.subscribe(setState);

    return () => {
      moUiStore.unsubscribe(setState);
    };
  }, []);

  useEffect(() => {
    if (optionSets) {
      const [firstMenuOptionSet] = optionSets;
      const [btnType, btnEnabled] = getBtnConfig(
        firstMenuOptionSet,
        0,
        optionSets.length,
      );

      moUiStore.set({
        ...state,
        menuOptionSets: optionSets,
        menuOptionSetCurrent: firstMenuOptionSet,
        progressBtnEnabled: btnEnabled,
        progressBtnType: btnType,
      });
    }
  }, []);

  const getCurrentIndex = (history): number => history[history.length - 1];

  const getNextOptionSetIndex = (): number => {
    const nextMenuItemOptionSetIds = state.optionsDraft.map(
      (o) => o.NextMenuItemOptionSetId,
    );
    const nextOptionSetId: number | undefined = _max(nextMenuItemOptionSetIds);

    switch (nextOptionSetId) {
      case undefined:
      case null:
        return state.indexCurrent + 1;
      case -1:
        return state.menuOptionSets.length;
      default: {
        const optionSetIdToIndex = Object.entries(state.menuOptionSets).reduce(
          (acc, [key, option]) => {
            acc[option.MenuItemOptionSetId] = parseInt(key);
            return acc;
          },
          {},
        );

        return optionSetIdToIndex[nextOptionSetId];
      }
    }
  };

  const getBtnConfig = (
    optionSet,
    optionSetIndex,
    optionSetLength,
  ): [ProgressBtnType, boolean] => {
    const isOptionsComplete = optionSetIndex === optionSetLength;
    const isSingleChoice =
      optionSet?.MinSelectCount === 1 && optionSet?.MaxSelectCount === 1;

    if (isOptionsComplete) {
      return [ProgressBtnType.ADD_TO_BASKET, true];
    }

    if (isSingleChoice) {
      return [ProgressBtnType.NONE, false];
    }

    return [ProgressBtnType.NEXT, optionSet?.MinSelectCount === 0];
  };

  const actions = useMemo(
    () => ({
      goBack: () => {
        const prevIndexHistory = [...state.indexHistory].slice(0, -1);
        const prevOptionsHistory = [...state.optionsHistory].slice(0, -1);
        const prevOptionsSelected = prevOptionsHistory.reduce(
          (acc, set) => [...acc, ...set],
          [],
        );
        const prevIndex = getCurrentIndex(prevIndexHistory);
        const prevMenuOptionSet = state.menuOptionSets[prevIndex];
        const [btnType, btnEnabled] = getBtnConfig(
          prevMenuOptionSet,
          prevIndex,
          state.menuOptionSets.length,
        );

        const newState = {
          ...state,
          indexCurrent: prevIndex,
          indexHistory: prevIndexHistory,
          optionsDraft: [],
          optionsHistory: prevOptionsHistory,
          optionsSelected: prevOptionsSelected,
          menuOptionSetCurrent: prevMenuOptionSet,
          progressBtnEnabled: btnEnabled,
          progressBtnType: btnType,
        };

        return moUiStore.set(newState);
      },
      goForward: () => {
        const nextIndex = getNextOptionSetIndex();
        const newIndexHistory = [...state.indexHistory, nextIndex];
        const nextMenuOptionSet = state.menuOptionSets[nextIndex];
        const [btnType, btnEnabled] = getBtnConfig(
          nextMenuOptionSet,
          nextIndex,
          state.menuOptionSets.length,
        );
        const newOptions = state.optionsDraft.sort(
          (a, b) => a.DisplayOrder - b.DisplayOrder,
        );

        const newState = {
          ...state,
          indexCurrent: getCurrentIndex(newIndexHistory),
          indexHistory: newIndexHistory,
          optionsDraft: [],
          optionsHistory: [...state.optionsHistory, [...newOptions]],
          optionsSelected: [...state.optionsSelected, ...newOptions],
          menuOptionSetCurrent: nextMenuOptionSet,
          progressBtnEnabled: btnEnabled,
          progressBtnType: btnType,
        };

        return moUiStore.set(newState);
      },
      resetUiState: () => {
        moUiStore.set(initialState);
      },
      setBtnEnabled: (btnEnabled: boolean) =>
        moUiStore.set({
          ...state,
          progressBtnEnabled: btnEnabled,
        }),
      setTestState: (customState) =>
        moUiStore.set({
          ...state,
          ...customState,
        }),
      updateDraftOptions: (optionItems) =>
        moUiStore.set({
          ...state,
          optionsDraft: optionItems,
        }),
    }),
    [state],
  );

  return {
    state,
    actions,
  };
};
